import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import styles from './searchPage.module.css';
import { searchAll } from '../../api/search';
import avatarPlaceholder from '../../assets/icons/avatar.svg';
import { buildDisplayName } from '../../utils/userDisplay';
import { getRoleLabel, participatesInRating } from '../../utils/roles';

const SCOPES = [
  { value: 'all', label: 'Все' },
  { value: 'users', label: 'Пользователи' },
  { value: 'teams', label: 'Команды' },
];

const SORTS = [
  { value: 'relevance', label: 'По релевантности' },
  { value: 'rank_asc', label: 'По рейтингу (лучшие)' },
  { value: 'rank_desc', label: 'По рейтингу (ниже)' },
  { value: 'name_asc', label: 'По имени (А-Я)' },
  { value: 'name_desc', label: 'По имени (Я-А)' },
];

function sortItems(items, sort) {
  const sorted = [...items];
  if (sort === 'rank_asc') {
    sorted.sort((a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER));
  } else if (sort === 'rank_desc') {
    sorted.sort((a, b) => (b.rank ?? -1) - (a.rank ?? -1));
  } else if (sort === 'name_asc') {
    sorted.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
  } else if (sort === 'name_desc') {
    sorted.sort((a, b) => b.title.localeCompare(a.title, 'ru'));
  }
  return sorted;
}

export function SearchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [scope, setScope] = useState('all');
  const [sort, setSort] = useState('relevance');
  const [skillFilter, setSkillFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const query = (params.get('q') || '').trim();
  const skillFromUrl = (params.get('skill') || '').trim();

  useEffect(() => {
    setSkillFilter(skillFromUrl);
  }, [skillFromUrl]);

  useEffect(() => {
    if (query.length < 2) {
      setUsers([]);
      setTeams([]);
      setError('');
      return;
    }

    async function load() {
      setLoading(true);
        setError('');
      try {
        const response = await searchAll(query, 25, skillFilter);
        setUsers(response.users || []);
        setTeams(response.teams || []);
      } catch (err) {
        setError(err.message || 'Не удалось выполнить поиск.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [query, skillFilter]);

  const userCards = useMemo(
    () =>
      users.map((item) => ({
        kind: 'user',
        id: `user-${item.user_id}`,
        href: `/search/users/${item.user_id}?q=${encodeURIComponent(query)}${skillFilter.trim() ? `&skill=${encodeURIComponent(skillFilter.trim())}` : ''}`,
        title: buildDisplayName(item) || `@${item.login}`,
        subtitle: `@${item.login}`,
        meta: `${getRoleLabel(item.role)}${participatesInRating(item.role) && item.rank_position ? ` · место #${item.rank_position}` : ''}`,
        image: item.avatar_url || avatarPlaceholder,
        rank: item.rank_position ?? null,
        skills: item.skills || [],
        wantTeam: Boolean(item.want_team),
      })),
    [users, query, skillFilter]
  );

  const teamCards = useMemo(
    () =>
      teams.map((item) => ({
        kind: 'team',
        id: `team-${item.team_id}`,
        href: `/search/teams/${item.team_id}?q=${encodeURIComponent(query)}${skillFilter.trim() ? `&skill=${encodeURIComponent(skillFilter.trim())}` : ''}`,
        title: item.name,
        subtitle: item.captain_login ? `Капитан: @${item.captain_login}` : 'Капитан не назначен',
        meta: `Участников: ${item.member_count} · место #${item.rank_position}`,
        image: item.avatar_url || avatarPlaceholder,
        rank: item.rank_position ?? null,
      })),
    [teams, query, skillFilter]
  );

  const cards = useMemo(() => {
    const base =
      scope === 'users' ? userCards : scope === 'teams' ? teamCards : [...userCards, ...teamCards];
    return sortItems(base, sort);
  }, [scope, sort, userCards, teamCards]);

  const hasQuery = query.length >= 2;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/profile');
  };

  return (
    <section className={styles.page}>
      <button type="button" className={styles.backButton} onClick={handleBack}>
        Назад
      </button>
      <div className={styles.head}>
        <h1 className={styles.title}>Расширенный поиск</h1>
        <p className={styles.subtitle}>
          {hasQuery ? `Запрос: "${query}"` : 'Введи минимум 2 символа в верхнем поиске.'}
        </p>
      </div>

      <div className={styles.filters}>
        <label className={styles.filterItem}>
          Область
          <select value={scope} onChange={(e) => setScope(e.target.value)}>
            {SCOPES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className={styles.filterItem}>
          Сортировка
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className={styles.filterItem}>
          Навык
          <input
            type="text"
            value={skillFilter}
            onChange={(e) => setSkillFilter(e.target.value)}
            placeholder="Например: Python"
            maxLength={64}
          />
        </label>
        <div className={styles.stats}>
          <span>Пользователей: {users.length}</span>
          <span>Команд: {teams.length}</span>
          <span>Всего: {cards.length}</span>
        </div>
      </div>

      {loading ? <p className={styles.note}>Ищем...</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      {!loading && hasQuery && !error && !cards.length ? (
        <p className={styles.note}>Ничего не найдено. Попробуй другой запрос или фильтр.</p>
      ) : null}

      <div className={styles.grid}>
        {cards.map((item) => (
          <Link key={item.id} to={item.href} className={styles.card}>
            <img
              className={styles.avatar}
              src={item.image}
              alt=""
              onError={(event) => {
                event.currentTarget.src = avatarPlaceholder;
              }}
            />
            <div className={styles.cardBody}>
              <div className={styles.cardHead}>
                <p className={styles.cardTitle}>{item.title}</p>
                <span className={styles.badge}>{item.kind === 'team' ? 'Команда' : 'Пользователь'}</span>
              </div>
              {item.kind === 'user' && item.wantTeam ? (
                <span className={styles.wantTeamBadge}>Хочу в команду</span>
              ) : null}
              <p className={styles.cardSubtitle}>{item.subtitle}</p>
              <p className={styles.cardMeta}>{item.meta}</p>
              {item.kind === 'user' && item.skills?.length ? (
                <div className={styles.skillRow}>
                  {item.skills.slice(0, 4).map((skill) => (
                    <span className={styles.skillChip} key={`${item.id}-${skill}`}>{skill}</span>
                  ))}
                </div>
              ) : null}
              <span className={styles.openHint}>Открыть →</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
